import React from 'react';
import { Box, Card, CardActions, CardContent, Chip, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Logo mapping for all integrations
const INTEGRATION_LOGOS = {
    viator: '/integrations/viator.png',
    tripadvisor: '/integrations/tripadvisor.png',
    expedia: '/integrations/expedia.png',
    ga4: '/integrations/ga4.png',
    facebook: '/integrations/facebook.svg',
    tiktok: '/integrations/tiktok.png',
    linkedin: '/integrations/linkedin.png',
    twitter: '/integrations/twitter.svg',
    snapchat: '/integrations/snapchat.svg',
    pinterest: '/integrations/pinterest.png',
    activiter: '/integrations/activiter.png',
    dynamic_pricing: '/integrations/rtide.png',
};

// Display name mapping for special cases
export const INTEGRATION_NAMES = {
    ga4: 'Google Analytics 4',
    facebook: 'Meta Pixel',
    tiktok: 'TikTok',
    linkedin: 'LinkedIn',
    twitter: 'X (Twitter)',
    snapchat: 'Snapchat',
    pinterest: 'Pinterest',
    viator: 'Viator',
    tripadvisor: 'TripAdvisor',
    expedia: 'Expedia Local Expert',
    activiter: 'Activiter',
    dynamic_pricing: 'Rising Tide',
};

function IntegrationCard({ integration, compact = false, onClick = false, children }) {
    const { t } = useTranslation();

    const logoSrc = INTEGRATION_LOGOS[integration.type] || '/integrations/tripadvisor.png';
    const displayName =
        INTEGRATION_NAMES[integration.type] ||
        integration.type.charAt(0).toUpperCase() + integration.type.slice(1);

    const statusChip = (
        <Chip
            label={integration.enabled ? t('common.enabled') : t('common.disabled')}
            color={integration.enabled ? 'success' : 'default'}
            size="small"
            sx={{ flexShrink: 0 }}
        />
    );

    // Compact variant (edit-page sidebar): a clean left-aligned header (logo + name
    // with the status chip) over a divided toggle row, instead of centered content
    // that leaves the block looking sparse.
    if (compact) {
        return (
            <Card
                sx={{
                    textAlign: 'left',
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 12px rgba(0, 0, 0, 0.05)',
                    height: 'auto',
                }}
            >
                <CardContent sx={{ p: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 1.5,
                        }}
                    >
                        <img
                            src={logoSrc}
                            alt={displayName}
                            style={{
                                height: 52,
                                width: 'auto',
                                maxWidth: 160,
                                objectFit: 'contain',
                            }}
                        />
                        {statusChip}
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {displayName}
                    </Typography>
                </CardContent>
                {children && (
                    <>
                        <Divider />
                        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            {children}
                        </Box>
                    </>
                )}
            </Card>
        );
    }

    return (
        <Card
            sx={{
                textAlign: 'center',
                p: 0,
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 12px rgba(0, 0, 0, 0.05)',
                // The edit-page (compact) card sits beside a tall form on desktop, so
                // match its height there; on mobile it stacks above the form, so hug
                // its content instead of leaving a big empty block.
                height: compact ? { xs: 'auto', md: '100%' } : '100%',
                cursor: onClick ? 'pointer' : 'default',
            }}
            onClick={
                onClick
                    ? (e) => {
                          e.stopPropagation();
                          onClick(e);
                      }
                    : null
            }
        >
            <CardContent sx={{ position: 'relative', p: compact ? 2 : 3 }}>
                <img
                    src={logoSrc}
                    alt={displayName}
                    style={{
                        width: compact ? '40%' : '30%',
                        aspectRatio: '1',
                        objectFit: 'contain',
                        marginBottom: compact ? 8 : 16,
                    }}
                />
                <Typography
                    variant={compact ? 'subtitle1' : 'h6'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                >
                    {displayName}
                </Typography>
                <Chip
                    label={integration.enabled ? t('common.enabled') : t('common.disabled')}
                    color={integration.enabled ? 'success' : 'default'}
                    size="small"
                    sx={{ mt: 1, position: 'absolute', top: 16, right: 16 }}
                />
            </CardContent>
            {children && (
                <CardActions sx={{ justifyContent: 'center', mb: 2 }}>{children}</CardActions>
            )}
        </Card>
    );
}

export default IntegrationCard;
